import React from 'react';
import { BarChart2, Activity } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

/**
 * Компонент боковой панели для быстрой настройки параметров ИМТ
 */
const QuickAdjustmentSidebar = ({ 
  formData,
  setFormData,
  errors,
  isCalculating
}) => {
  // Расчет текущего веса
  const weight = parseFloat(formData.weight) || 0;
  
  // Расчет текущего роста
  const height = parseFloat(formData.height) || 0;

  // Расчет текущего возраста
  const age = parseInt(formData.age) || 0;
  
  // Обработчик изменения веса
  const handleWeightChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      weight: newValue
    }));
  };
  
  // Обработчик изменения роста
  const handleHeightChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      height: newValue
    }));
  };
  
  // Обработчик изменения возраста
  const handleAgeChange = (e) => {
    const newValue = e.target.value;
    setFormData(prev => ({
      ...prev,
      age: newValue
    }));
  };

  // Функция для вычисления пределов на основе системы измерения
  const getLimits = () => {
    if (formData.measurementSystem === 'metric') {
      return {
        weight: { min: 30, max: 200, step: 1 },
        height: { min: 100, max: 220, step: 1 },
      };
    } else {
      return {
        weight: { min: 66, max: 440, step: 1 }, // фунты
        height: { min: 39, max: 87, step: 1 },  // дюймы
      };
    }
  };

  const limits = getLimits();
  
  // Предопределенные типы телосложения для быстрого выбора
  const predefinedBodyTypes = [
    { value: 'asthenic', label: 'Астенический' },
    { value: 'normal', label: 'Нормостенический' },
    { value: 'hypersthenic', label: 'Гиперстенический' }
  ];
  
  // Предопределенные уровни активности для быстрого выбора
  const predefinedActivityLevels = [
    { value: 'sedentary', label: 'Малоподвижный' },
    { value: 'moderate', label: 'Умеренный' },
    { value: 'active', label: 'Активный' }
  ];
  
  // Функция расчета
  const calculateBmi = () => {
    // Здесь должна быть вызвана функция основного расчета
    // В данном случае просто эмулируем действие, так как основная логика 
    // находится в родительском компоненте
    document.querySelector('button[type="submit"]')?.click();
  };

  // Шаблоны параметров для быстрого выбора
  const quickTemplates = [
    {
      name: 'Мужчина, 30 лет',
      apply: () => setFormData(prev => ({
        ...prev,
        gender: 'male',
        age: '30',
        height: formData.measurementSystem === 'metric' ? '175' : '69',
        weight: formData.measurementSystem === 'metric' ? '75' : '165',
        activityLevel: 'moderate'
      }))
    },
    {
      name: 'Женщина, 30 лет',
      apply: () => setFormData(prev => ({
        ...prev,
        gender: 'female',
        age: '30',
        height: formData.measurementSystem === 'metric' ? '165' : '65',
        weight: formData.measurementSystem === 'metric' ? '60' : '132',
        activityLevel: 'moderate'
      }))
    },
    {
      name: 'Подросток, 16 лет',
      apply: () => setFormData(prev => ({
        ...prev,
        gender: 'male',
        age: '16',
        height: formData.measurementSystem === 'metric' ? '170' : '67',
        weight: formData.measurementSystem === 'metric' ? '65' : '143',
        activityLevel: 'active'
      }))
    },
    {
      name: 'Пожилой, 65 лет',
      apply: () => setFormData(prev => ({
        ...prev,
        gender: 'male',
        age: '65',
        height: formData.measurementSystem === 'metric' ? '173' : '68',
        weight: formData.measurementSystem === 'metric' ? '80' : '176',
        activityLevel: 'light'
      }))
    }
  ];
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-5 shadow-sm">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4 flex items-center">
        <Activity className="w-5 h-5 mr-2 text-emerald-500" />
        Быстрая настройка параметров
      </h3>
      
      <div className="space-y-5">
        {/* Быстрый выбор шаблонов */}
        <div>
          <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
            Быстрый выбор
          </div>
          <div className="grid grid-cols-2 gap-2">
            {quickTemplates.map((template, index) => (
              <button
                key={index}
                type="button"
                onClick={template.apply}
                className="px-3 py-2 text-xs rounded-lg border bg-gray-50 hover:bg-gray-100 text-gray-700 border-gray-200 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600 transition-colors"
              >
                {template.name}
              </button>
            ))}
          </div>
        </div>

        {/* Вес */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="weightSlider" className="text-gray-600 dark:text-gray-400">
              {formData.measurementSystem === 'metric' ? 'Вес (кг)' : 'Вес (фунты)'}
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {weight} {formData.measurementSystem === 'metric' ? 'кг' : 'фнт'}
            </span>
          </div>
          <input
            type="range"
            id="weightSlider"
            min={limits.weight.min}
            max={limits.weight.max}
            step={limits.weight.step}
            value={weight}
            onChange={handleWeightChange}
            aria-label={formData.measurementSystem === 'metric' ? 'Вес в килограммах' : 'Вес в фунтах'}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Кнопки с предопределенными весами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {formData.measurementSystem === 'metric' ? (
              <>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, weight: '50' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.weight === '50' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  50 кг
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, weight: '60' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.weight === '60' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  60 кг
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, weight: '70' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.weight === '70' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  70 кг
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, weight: '80' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.weight === '80' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  80 кг
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, weight: '110' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.weight === '110' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  110 фнт
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, weight: '132' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.weight === '132' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  132 фнт
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, weight: '154' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.weight === '154' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  154 фнт
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, weight: '176' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.weight === '176' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  176 фнт
                </button>
              </>
            )}
          </div>
        </div>
        
        {/* Рост */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="heightSlider" className="text-gray-600 dark:text-gray-400">
              {formData.measurementSystem === 'metric' ? 'Рост (см)' : 'Рост (дюймы)'}
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {height} {formData.measurementSystem === 'metric' ? 'см' : 'дюйм'}
            </span>
          </div>
          <input
            type="range"
            id="heightSlider"
            min={limits.height.min}
            max={limits.height.max}
            step={limits.height.step}
            value={height}
            onChange={handleHeightChange}
            aria-label={formData.measurementSystem === 'metric' ? 'Рост в сантиметрах' : 'Рост в дюймах'}
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Кнопки с предопределенными ростом */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            {formData.measurementSystem === 'metric' ? (
              <>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, height: '160' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.height === '160' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  160 см
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, height: '170' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.height === '170' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  170 см
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, height: '180' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.height === '180' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  180 см
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, height: '190' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.height === '190' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  190 см
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, height: '63' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.height === '63' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  63"
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, height: '67' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.height === '67' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  67"
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, height: '71' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.height === '71' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  71"
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, height: '75' }))}
                  className={`px-2 py-1 text-xs rounded border transition-colors
                    ${formData.height === '75' 
                      ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                      : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                    }`}
                >
                  75"
                </button>
              </>
            )}
          </div>
        </div>

        {/* Возраст */}
        <div>
          <div className="flex justify-between items-center mb-1 text-sm">
            <label htmlFor="ageSlider" className="text-gray-600 dark:text-gray-400">
              Возраст (лет)
            </label>
            <span className="font-medium text-gray-900 dark:text-white">
              {age} лет
            </span>
          </div>
          <input
            type="range"
            id="ageSlider"
            min="1"
            max="100"
            step="1"
            value={age}
            onChange={handleAgeChange}
            aria-label="Возраст в годах"
            className="w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-lg appearance-none cursor-pointer accent-emerald-600"
          />
          
          {/* Кнопки с предопределенными возрастами */}
          <div className="grid grid-cols-4 gap-1 mt-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, age: '18' }))}
              className={`px-2 py-1 text-xs rounded border transition-colors
                ${formData.age === '18' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              18 лет
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, age: '30' }))}
              className={`px-2 py-1 text-xs rounded border transition-colors
                ${formData.age === '30' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              30 лет
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, age: '45' }))}
              className={`px-2 py-1 text-xs rounded border transition-colors
                ${formData.age === '45' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              45 лет
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, age: '65' }))}
              className={`px-2 py-1 text-xs rounded border transition-colors
                ${formData.age === '65' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              65 лет
            </button>
          </div>
        </div>
        
        {/* Выбор пола */}
        <div>
          <div className="flex justify-between items-center mb-2 text-sm">
            <label className="text-gray-600 dark:text-gray-400">Пол</label>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, gender: 'male' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.gender === 'male' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Мужской</div>
            </button>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, gender: 'female' }))}
              className={`p-2 text-sm rounded-lg border transition-colors
                ${formData.gender === 'female' 
                  ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                  : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                }`}
            >
              <div className="font-medium">Женский</div>
            </button>
          </div>
        </div>
        
        {/* Тип телосложения */}
        <div>
          <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
            Тип телосложения
          </div>
          <div className="grid grid-cols-3 gap-2">
            {predefinedBodyTypes.map(type => (
              <button
                key={type.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, bodyType: type.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.bodyType === type.value 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {type.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Уровень активности */}
        <div>
          <div className="text-sm text-gray-700 dark:text-gray-300 mb-2">
            Уровень активности
          </div>
          <div className="grid grid-cols-3 gap-2">
            {predefinedActivityLevels.map(level => (
              <button
                key={level.value}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, activityLevel: level.value }))}
                className={`px-2 py-1 text-xs rounded border transition-colors
                  ${formData.activityLevel === level.value 
                    ? 'bg-emerald-100 border-emerald-300 text-emerald-700 dark:bg-emerald-900/30 dark:border-emerald-700 dark:text-emerald-300'
                    : 'bg-gray-50 border-gray-200 text-gray-700 hover:bg-gray-100 dark:bg-gray-700 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-600'
                  }`}
              >
                {level.label}
              </button>
            ))}
          </div>
        </div>
        
        {/* Кнопка пересчета */}
        <Button
          primary
          fullWidth
          onClick={calculateBmi}
          icon={<BarChart2 className="w-4 h-4" />}
          disabled={isCalculating || Object.keys(errors).length > 0}
          isLoading={isCalculating}
        >
          Рассчитать ИМТ
        </Button>
      </div>
    </div>
  );
};

export default QuickAdjustmentSidebar;